import MetadataConverter, { MetadataConverterOptions } from 'titus-ts/dist/js/Util/Metadata/Converters';
import { MetadataObject } from 'titus-ts/dist/js/Util/Metadata/MetadataObject';
import { DictionaryInterface } from 'titus-ts/dist/js/Common/Util/DictionaryInterface';
import { OfficeApi } from '../office/OfficeApi';
import { AppSettingsService } from './appSettingsService';
import MetadataService from './metadataService';

export class OutlookMetadataService implements MetadataService {
    private officeApi: OfficeApi;
    private appSettingsService?: AppSettingsService;

    public constructor(officeApi: OfficeApi, appSettingsService?: AppSettingsService) {
        this.officeApi = officeApi;
        this.appSettingsService = appSettingsService;
    }

    public getCurrentMetadata = async (fallback?: boolean): Promise<MetadataObject | undefined> => {
        console.log('>>getCurrentMetadata (outlook)...');
        const emailHeaders = await this.officeApi.getInternetHeadersAsync(MetadataConverter.getMetadataXHeaderNames());
        console.log(`obtained email Headers: ${JSON.stringify(emailHeaders)}`);
        const metadata = await this.convertHeadersToMetadata(emailHeaders);
        const currentOrOriginalMetadata = metadata ?? (fallback ? await this.getOriginalMetadata() : undefined);

        console.log(`<<getCurrentMetadata (outlook):${currentOrOriginalMetadata}`);
        return currentOrOriginalMetadata;
    };

    public getOriginalMetadata = async (): Promise<MetadataObject | undefined> => {
        console.log('>>getOriginalMetadata (outlook)...');
        const inReplyToId = await this.officeApi.getInReplyToAsync();
        if (!inReplyToId) {
            return undefined;
        }

        const emailHeaders = await this.officeApi.getOriginalItemInternetHeaders(
            inReplyToId,
            MetadataConverter.getMetadataXHeaderNames(),
        );
        const metadata = await this.convertHeadersToMetadata(emailHeaders);

        console.log(`<<getOriginalMetadata (outlook):${metadata}`);
        return metadata;
    };

    public writeMetadata = async (metadata: MetadataObject): Promise<void> => {
        const configuration = await this.getMetadataConfiguration();
        const headers = MetadataConverter.createXHeadersFromMetadata(metadata, configuration);
        await this.officeApi.setInternetHeadersAsync(headers);
    };

    private convertHeadersToMetadata = async (
        emailHeaders: DictionaryInterface<string>,
    ): Promise<MetadataObject | undefined> => {
        try {
            const configuration = await this.getMetadataConfiguration();
            const metadata = MetadataConverter.parseMetadataFromXHeaders(emailHeaders, configuration);
            return Object.keys(metadata).length > 0 ? metadata : undefined;
        } catch (error) {
            console.error('Unable to read metadata from Outlook x-headers. Treating the item as unlabelled.', error);
            return undefined;
        }
    };

    private getMetadataConfiguration = async (): Promise<MetadataConverterOptions> => {
        const appSettings = await this.appSettingsService?.getApplicationSettings();
        const metadataSettings = appSettings?.AddinMetadataHandlers;
        const v3MetadataSupport = metadataSettings?.V3MetadataSupport;
        let shouldReadV3Metadata: boolean | undefined;
        let shouldWriteV3Metadata: boolean | undefined;

        switch (v3MetadataSupport) {
            case 'Off':
                shouldReadV3Metadata = false;
                shouldWriteV3Metadata = false;
                break;
            case 'Read':
                shouldReadV3Metadata = true;
                shouldWriteV3Metadata = false;
                break;
            case 'ReadWrite':
                shouldReadV3Metadata = true;
                shouldWriteV3Metadata = true;
                break;
        }

        return {
            v4: {
                schemaNamespace: appSettings?.ConfigurationSettings?.ConfigurationNamespace,
            },
            v3: {
                shouldReadMetadata: shouldReadV3Metadata,
                shouldWriteMetadata: shouldWriteV3Metadata,
                rootPropertyValue: metadataSettings?.V3PropertyRoot || undefined,
                multiSelectValueSeparator: metadataSettings?.V3MultiSelectTokenSeparator || undefined,
            },
        };
    };

}
